import { Box } from "@mui/material";
import IFCViewer from "./IFCViewer";
import { forwardRef, useEffect, useRef, useCallback } from "react";
import * as THREE from "three";
import { rotateTo, zoomTo } from "./CameraControls";

const SideBySideViewer = forwardRef(
  ({ modelURL, backgroundModels, leftTitle, rightTitle, elementIds, viewFilter, selectComponents, onSelectionChange }, ref) => {
    const {
      planHide = [],
      actualHide = [],
      delayedNotStart = [],
      planProgress = [],
      actualProgress = [],
      delayed = [],
      planComplete = [],
      actualComplete = [],
      showFilter,
      all = [],
      highlight = [],
      hideElementIds = [],
    } = elementIds;
    const commonProps = {
      modelURL,
      backgroundModels,
      showFilterElementIds: showFilter,
      allElementIds: all,
      highlightElementIds: highlight,
      onSelectionChange,
    };

    const leftViewerRef = useRef();
    const rightViewerRef = useRef();

    const syncCameras = (sourceViewer, targetViewer) => {
      sourceViewer.world.camera.controls.addEventListener("control", () => {
        const position = sourceViewer.world.camera.controls.getPosition(new THREE.Vector3());
        const target = sourceViewer.world.camera.controls.getTarget(new THREE.Euler());
        targetViewer.world.camera.controls.setLookAt(position.x, position.y, position.z, target.x, target.y, target.z);
      });
    };

    useEffect(() => {
      if (leftViewerRef.current && rightViewerRef.current) {
        syncCameras(leftViewerRef.current, rightViewerRef.current);
        syncCameras(rightViewerRef.current, leftViewerRef.current);

        return () => {
          if (leftViewerRef.current && rightViewerRef.current) {
            leftViewerRef.current.world.camera.controls.removeEventListener("control");
            rightViewerRef.current.world.camera.controls.removeEventListener("control");
          }
        };
      }
    }, [leftViewerRef, rightViewerRef]);

    useEffect(() => {
      if (ref) {
        ref.current = {
          left: leftViewerRef.current,
          right: rightViewerRef.current,
        };
      }
      if (viewFilter === "Current view" || viewFilter === "Plan view") {
        homeViewOnClick();
      } else if (viewFilter === "Plan view") {
        rotateToViewOnClick("Top");
      }
    }, [ref, leftViewerRef, rightViewerRef, viewFilter]);

    const handleSelectChange = useCallback(
      (value, name) => {
        if (onSelectionChange) {
          onSelectionChange(value, name);
        }
      },
      [onSelectionChange]
    );

    const homeViewOnClick = () => {
      if (leftViewerRef.current && rightViewerRef.current) {
        zoomTo(leftViewerRef.current.world.camera, leftViewerRef.current.mainModels);
        zoomTo(rightViewerRef.current.world.camera, rightViewerRef.current.mainModels);
      }
    };

    const rotateToViewOnClick = (side) => {
      rotateTo(leftViewerRef.current.world.camera, side);
      rotateTo(rightViewerRef.current.world.camera, side);
    };

    return (
      <Box display="flex" width={"100%"} height={"100%"} gap={"0.5%"}>
        <Box width={"49.5%"} height={"100%"}>
          <IFCViewer
            ref={leftViewerRef}
            title={leftTitle}
            hideElementIds={[...planHide, ...hideElementIds]}
            inProgressElementIds={planProgress}
            selectComponents={selectComponents}
            onSelectionChange={handleSelectChange}
            homeViewOnClick={homeViewOnClick}
            rotateToViewOnClick={rotateToViewOnClick}
            advanced
            {...commonProps}
          />
        </Box>
        <img src={"assets/illustrations/verticaldivider.svg"} style={{ height: "100%" }} alt={"divider"} />
        <Box width={"49.5%"} height={"100%"}>
          <IFCViewer
            ref={rightViewerRef}
            title={rightTitle}
            hideElementIds={[...actualHide, ...hideElementIds]}
            delayedNotStartElementIds={delayedNotStart}
            inProgressElementIds={actualProgress}
            delayedElementIds={delayed}
            advanced
            {...commonProps}
          />
        </Box>
      </Box>
    );
  }
);

export default SideBySideViewer;
